import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { LayoutDashboard, Users, ClipboardList, Target, TrendingUp, CheckCircle2, AlertCircle, XCircle, Copy, IndianRupee } from "lucide-react";
import DashboardLayout, { KpiCard } from "@/components/DashboardLayout";
import { api, formatError } from "@/lib/api";
import { StatusBadge } from "@/components/Brand";
import { MakePaymentBody } from "@/components/MakePayment";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";

const nav = [
  { to: "/employee", label: "Dashboard", key: "dashboard", icon: LayoutDashboard, end: true },
  { to: "/employee/merchants", label: "My Merchants", key: "merchants", icon: Users },
  { to: "/employee/leads", label: "My Leads", key: "leads", icon: ClipboardList },
  { to: "/employee/make-payment", label: "Make Payment", key: "make-payment", icon: IndianRupee },
  { to: "/employee/targets", label: "Targets", key: "targets", icon: Target },
];

function Home() {
  const [s, setS] = useState(null); const [t, setT] = useState([]);
  useEffect(() => { api.get("/reports/summary").then((r) => setS(r.data)); api.get("/targets").then((r) => setT(r.data)); }, []);
  const thisMonth = new Date().toISOString().slice(0,7);
  const currentTarget = t.find((x) => x.month === thisMonth);
  const achievedPct = currentTarget && currentTarget.target_leads > 0 ? Math.min(100, Math.round(((s?.approved || 0) / currentTarget.target_leads) * 100)) : 0;
  return (
    <DashboardLayout roleLabel="Employee Panel" nav={nav} title="Dashboard" subtitle="Sales, Target और Performance">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="e-sales" label="Sales (Approved)" value={s?.approved ?? "—"} accent="green" icon={CheckCircle2} />
        <KpiCard testId="e-target" label="Target Leads" value={currentTarget?.target_leads ?? "—"} accent="blue" icon={Target} />
        <KpiCard testId="e-achieved" label="Achieved %" value={`${achievedPct}%`} accent="orange" icon={TrendingUp} />
        <KpiCard testId="e-pending" label="Pending" value={s?.pending ?? "—"} accent="orange" icon={AlertCircle} />
        <KpiCard testId="e-rejected" label="Rejected" value={s?.rejected ?? "—"} accent="red" icon={XCircle} />
        <KpiCard testId="e-duplicate" label="Duplicate" value={s?.duplicate ?? "—"} accent="red" icon={Copy} />
      </div>
      {currentTarget && (
        <div className="mt-6 bg-white border rounded-md p-6">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-medium">Monthly Target · {currentTarget.month}</div>
          <div className="mt-3 flex items-center justify-between text-sm">
            <span>{s?.approved || 0} / {currentTarget.target_leads} approved leads</span>
            <span className="font-medium">{achievedPct}%</span>
          </div>
          <Progress value={achievedPct} className="mt-2" />
        </div>
      )}
    </DashboardLayout>
  );
}

function MerchantsPage() {
  const [rows, setRows] = useState([]); const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ mobile: "", password: "", name: "", email: "", role: "merchant", business_name: "" });
  const load = () => api.get("/users", { params: { role: "merchant" } }).then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/users", form); toast.success("Merchant created"); setOpen(false); setForm({ mobile: "", password: "", name: "", email: "", role: "merchant", business_name: "" }); load(); } catch (err) { toast.error(formatError(err)); } };
  return (
    <DashboardLayout roleLabel="Employee Panel" nav={nav} title="My Merchants" subtitle="अपने assigned merchants">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#1a3ba8] hover:bg-[#17348f]" data-testid="e-add-merch">+ Add Merchant</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Merchant</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Mobile</Label><Input required value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></div>
              <div><Label>Password</Label><Input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="col-span-2"><Label>Business</Label><Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></div>
              <DialogFooter className="col-span-2"><Button type="submit">Create</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Mobile</TableHead><TableHead>Business</TableHead><TableHead>KYC</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((u) => (<TableRow key={u.id}><TableCell>{u.name}</TableCell><TableCell className="font-mono text-sm">{u.mobile}</TableCell><TableCell>{u.business_name || "—"}</TableCell><TableCell>{u.kyc_status ? <StatusBadge status={u.kyc_status} /> : "—"}</TableCell></TableRow>))}
            {rows.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No merchants</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </DashboardLayout>
  );
}

function LeadsPage() {
  const [leads, setLeads] = useState([]); const [svcs, setSvcs] = useState([]); const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ service_id: "", customer_name: "", customer_mobile: "", customer_email: "", aadhaar_no: "", pan_no: "", address: "", state: "", district: "", pincode: "", notes: "" });
  const load = () => { api.get("/leads").then((r) => setLeads(r.data)); api.get("/services").then((r) => setSvcs(r.data)); api.get("/categories").then((r) => setCats(r.data)); };
  useEffect(() => { load(); }, []);
  const submit = async (e) => { e.preventDefault(); try { await api.post("/leads", form); toast.success("Lead submitted"); setOpen(false); setForm({ service_id: "", customer_name: "", customer_mobile: "", customer_email: "", aadhaar_no: "", pan_no: "", address: "", state: "", district: "", pincode: "", notes: "" }); load(); } catch (err) { toast.error(formatError(err)); } };
  const selectedSvc = svcs.find((s) => s.id === form.service_id);
  const selectedCat = selectedSvc ? cats.find((c) => c.id === selectedSvc.category_id) : null;
  const catName = (selectedCat?.name || "").toLowerCase();
  const isShort = ["accounts", "credit", "demat"].some((x) => catName.includes(x)) && !catName.includes("bc") && !catName.includes("csp");
  return (
    <DashboardLayout roleLabel="Employee Panel" nav={nav} title="My Leads" subtitle="अपने बनाए leads">
      <div className="flex justify-end mb-4">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-orange-gradient hover:opacity-90 text-white rounded-full" data-testid="e-add-lead">+ New Lead</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Submit Lead</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><Label>Service *</Label>
                <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                  <SelectTrigger data-testid="e-lead-svc"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{svcs.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Customer Name / नाम *</Label><Input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="e-lead-name" /></div>
              <div><Label>Mobile No / मोबाइल *</Label><Input required inputMode="numeric" value={form.customer_mobile} onChange={(e) => setForm({ ...form, customer_mobile: e.target.value })} data-testid="e-lead-mobile" /></div>
              <div className="md:col-span-2"><Label>Gmail ID / ईमेल</Label><Input type="email" value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} data-testid="e-lead-email" /></div>
              <div className="md:col-span-2"><Label>PAN Card No / पैन</Label><Input value={form.pan_no} onChange={(e) => setForm({ ...form, pan_no: e.target.value.toUpperCase() })} data-testid="e-lead-pan" /></div>
              {!isShort && (
                <>
                  <div><Label>Aadhaar Card No / आधार</Label><Input inputMode="numeric" value={form.aadhaar_no} onChange={(e) => setForm({ ...form, aadhaar_no: e.target.value })} data-testid="e-lead-aadhaar" /></div>
                  <div><Label>Pin Code / पिन कोड</Label><Input inputMode="numeric" value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} data-testid="e-lead-pincode" /></div>
                  <div className="md:col-span-2"><Label>Full Address / पूरा पता</Label><Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="e-lead-address" /></div>
                  <div><Label>State / राज्य</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="e-lead-state" /></div>
                  <div><Label>District / जिला</Label><Input value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} data-testid="e-lead-district" /></div>
                  <div className="md:col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="e-lead-notes" /></div>
                </>
              )}
              <DialogFooter className="md:col-span-2"><Button type="submit" disabled={!form.service_id} className="bg-brand-gradient text-white rounded-full px-6" data-testid="e-lead-submit">Submit</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Customer</TableHead><TableHead>Mobile</TableHead><TableHead>Service</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow></TableHeader>
          <TableBody>
            {leads.map((l) => { const s = svcs.find((x) => x.id === l.service_id); return (
              <TableRow key={l.id}><TableCell>{l.customer_name}</TableCell><TableCell className="font-mono text-sm">{l.customer_mobile}</TableCell><TableCell>{s?.name || l.service_id}</TableCell><TableCell><StatusBadge status={l.status} /></TableCell><TableCell className="text-xs text-slate-500">{(l.created_at || "").split("T")[0]}</TableCell></TableRow>
            ); })}
            {leads.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No leads yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </DashboardLayout>
  );
}

function TargetsPage() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/targets").then((r) => setRows(r.data)); }, []);
  return (
    <DashboardLayout roleLabel="Employee Panel" nav={nav} title="Targets" subtitle="Monthly targets की history">
      <div className="bg-white border rounded-md overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Month</TableHead><TableHead>Target Leads</TableHead><TableHead>Target Amount</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((t) => (<TableRow key={t.id}><TableCell>{t.month}</TableCell><TableCell>{t.target_leads}</TableCell><TableCell>₹ {Number(t.target_amount).toFixed(2)}</TableCell></TableRow>))}
            {rows.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-slate-500 py-8">No targets assigned</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </DashboardLayout>
  );
}

function EmployeeMakePaymentPage() {
  return (
    <DashboardLayout roleLabel="Employee Panel" nav={nav} title="Make Payment / भुगतान करें" subtitle="Admin के active QR पर pay करें और details submit करें">
      <MakePaymentBody />
    </DashboardLayout>
  );
}

export default function EmployeeDashboard() {
  return (
    <Routes>
      <Route index element={<Home />} />
      <Route path="merchants" element={<MerchantsPage />} />
      <Route path="leads" element={<LeadsPage />} />
      <Route path="make-payment" element={<EmployeeMakePaymentPage />} />
      <Route path="targets" element={<TargetsPage />} />
      <Route path="*" element={<Navigate to="/employee" replace />} />
    </Routes>
  );
}
